/**
 * Field Importance Generator
 * 
 * Automatically analyzes Fulcrum forms to categorize field importance and generate
 * PopupFields and fieldMapping configurations. This replaces manual configuration
 * with intelligent form analysis.
 */

import { fetchFulcrumForm, fetchFulcrumForms } from '../fulcrumLibService';
import { FieldMapping } from '../../types';
import { cacheFormSchema, getCachedFormSchema } from '../relatedItemsCache';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface FieldImportanceResult {
  fieldId: string;
  label: string;
  dataName: string;
  type: string;
  importance: 'high' | 'medium' | 'low' | 'hidden';
  category: 'identification' | 'description' | 'date' | 'location' | 'media' | 'relationship' | 'metadata' | 'other';
  reasoning: string;
  sampleValues?: string[];
}

export interface FormAnalysisResult {
  formId: string;
  formName: string;
  fields: FieldImportanceResult[];
  suggestedFieldMapping: FieldMapping;
  suggestedPopupFields: string[];
  titleField?: string;
  descriptionField?: string;
  dateFields: string[];
  mediaFields: string[];
  relationshipFields: string[];
}

export interface ConfigGenerationOptions {
  userProfile?: any;
  includeHiddenFields?: boolean;
  prioritizeUserFriendlyLabels?: boolean;
  includeMetadata?: boolean;
  maxPopupFields?: number;
}

// ============================================================================
// FIELD ANALYSIS PATTERNS (DRY: Consolidated Configuration)
// ============================================================================

/**
 * Consolidated field analysis patterns with importance and category mapping
 * This follows DRY principle by defining patterns once with their metadata
 */
const FIELD_ANALYSIS_CONFIG = {
  identification: {
    importance: 'high' as const,
    patterns: [
      /^(name|title|label)$/i,
      /site.*name/i,
      /location.*name/i,
      /entity.*name/i,
      /object.*name/i,
      /item.*name/i,
      /^(species|animal|plant)$/i,
      /job.*(id|description)/i,
      /trip.*(id|description)/i,
    ]
  },
  description: {
    importance: 'high' as const,
    patterns: [
      /^(description|notes|comment|details|info|summary)$/i,
      /.*description$/i,
      /.*notes$/i,
      /.*comment$/i,
    ]
  },
  date: {
    importance: 'high' as const,
    patterns: [
      /^(date|time|when|created|updated|scheduled)$/i,
      /.*date$/i,
      /.*time$/i,
      /recorded.*date/i,
      /scheduled.*(date|time)/i,
    ]
  },
  location: {
    importance: 'medium' as const,
    patterns: [
      /^(location|coordinates|lat|lng|latitude|longitude)$/i,
      /.*location$/i,
      /.*coordinates$/i,
    ]
  },
  media: {
    importance: 'medium' as const,
    patterns: [
      /^(photo|image|video|media|attachment)$/i,
      /.*photo$/i,
      /.*image$/i,
      /.*video$/i,
    ]
  },
  relationship: {
    importance: 'medium' as const,
    patterns: [
      /^(related|linked|reference)$/i,
      /.*link$/i,
      /.*reference$/i,
      /related.*/i,
    ]
  }
} as const;

/**
 * Field type configuration for consistent handling
 */
const FIELD_TYPE_CONFIG = {
  hidden: ['signature_field', 'barcode_field', 'hyperlink_field', 'calculation_field'],
  metadata: ['status_field', 'project_field', 'assigned_to_field'],
  media: ['photo_field', 'video_field'],
  relationship: ['record_link_field'],
  date: ['date_time_field', 'date_field', 'time_field'],
  choice: ['choice_field', 'classification_field']
} as const;

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

/**
 * Default configuration values for maintainability
 */
const DEFAULT_CONFIG = {
  MAX_POPUP_FIELDS: 8,
  DEFAULT_FIELD_MAPPING_FALLBACKS: ['id', 'fulcrum_id', '_record_id'] as string[],
  DEFAULT_DATE_FALLBACKS: ['created_at', 'client_created_at'] as string[],
  DEFAULT_GEOMETRY_FIELD: ['geometry'] as string[]
};

// ============================================================================
// CORE ANALYSIS FUNCTIONS
// ============================================================================

/**
 * Analyze a single field to determine its importance and category
 */
export const analyzeField = (element: any): FieldImportanceResult => {
  const fieldId = element.key;
  const label = element.label || element.data_name || fieldId;
  const dataName = element.data_name || fieldId;
  const type = element.type || 'text';
  
  let importance: 'high' | 'medium' | 'low' | 'hidden' = 'medium';
  let category: FieldImportanceResult['category'] = 'other';
  let reasoning = '';

  // Check for hidden field types
  if (FIELD_TYPE_CONFIG.hidden.includes(type)) {
    importance = 'hidden';
    category = 'metadata';
    reasoning = `Hidden field type: ${type}`;
    return { fieldId, label, dataName, type, importance, category, reasoning };
  }

  // Analyze by field label patterns using consolidated config (DRY improvement)
  for (const [categoryKey, config] of Object.entries(FIELD_ANALYSIS_CONFIG)) {
    if (config.patterns.some(pattern => pattern.test(label))) {
      importance = config.importance;
      category = categoryKey as FieldImportanceResult['category'];
      reasoning = `Identified as ${categoryKey} field`;
      break;
    }
  }

  // Analyze by field type using consolidated config (DRY improvement)
  if (FIELD_TYPE_CONFIG.media.includes(type)) {
    importance = 'medium';
    category = 'media';
    reasoning += (reasoning ? '; ' : '') + 'Media field type';
  } else if (FIELD_TYPE_CONFIG.relationship.includes(type)) {
    importance = 'medium';
    category = 'relationship';
    reasoning += (reasoning ? '; ' : '') + 'Record link field';
  } else if (FIELD_TYPE_CONFIG.date.includes(type)) {
    importance = 'high';
    category = 'date';
    reasoning += (reasoning ? '; ' : '') + 'Date/time field type';
  } else if (FIELD_TYPE_CONFIG.choice.includes(type)) {
    // Choice fields can be important if they're categorical data
    if (importance === 'medium') {
      reasoning += (reasoning ? '; ' : '') + 'Choice/classification field';
    }
  } else if (type === 'text_field') {
    // Text fields are often important for names/descriptions
    if (importance === 'medium' && FIELD_ANALYSIS_CONFIG.identification.patterns.some(pattern => pattern.test(label))) {
      importance = 'high';
      category = 'identification';
      reasoning = 'Text field matching name pattern';
    }
  } else if (FIELD_TYPE_CONFIG.metadata.includes(type)) {
    importance = 'low';
    category = 'metadata';
    reasoning = `Metadata field type: ${type}`;
  }

  // Default reasoning if none provided
  if (!reasoning) {
    reasoning = `Standard ${type} field`;
  }

  return { fieldId, label, dataName, type, importance, category, reasoning };
};

/**
 * Recursively walk through form elements to find all fields
 */
const walkFormElements = (elements: any[]): any[] => {
  const allFields: any[] = [];
  
  if (!Array.isArray(elements)) {
    return allFields;
  }
  
  for (const element of elements) {
    // Add current element if it has a key (is a field)
    if (element.key) {
      allFields.push(element);
    }
    
    // Recursively process nested elements (sections, repeatables)
    if (element.elements && Array.isArray(element.elements)) {
      allFields.push(...walkFormElements(element.elements));
    }
  }
  
  return allFields;
};

/**
 * Analyze a complete Fulcrum form to generate field importance rankings
 */
export const analyzeForm = async (
  formId: string, 
  userProfile: any,
  options: ConfigGenerationOptions = {}
): Promise<FormAnalysisResult> => {
  try {
    console.log(`[FieldImportanceGenerator] Analyzing form ${formId}`);
    
    // Fetch the form schema
    const formResponse = await fetchFulcrumForm(formId, userProfile);
    const form = formResponse.form || formResponse;
    
    if (!form || !form.elements) {
      throw new Error(`Invalid form schema for ${formId}`);
    }

    // Extract all fields from the form (including nested ones)
    const allElements = walkFormElements(form.elements);
    
    console.log(`[FieldImportanceGenerator] Found ${allElements.length} fields in form ${form.name}`);
    
    // Analyze each field
    const fields = allElements.map(analyzeField);
    
    // Filter out hidden fields if requested
    const visibleFields = options.includeHiddenFields 
      ? fields 
      : fields.filter(f => f.importance !== 'hidden');
    
    // Generate suggested configurations
    const highImportanceFields = visibleFields.filter(f => f.importance === 'high');
    const mediumImportanceFields = visibleFields.filter(f => f.importance === 'medium');
    
    // Suggest popup fields (limit to maxPopupFields)
    const maxFields = options.maxPopupFields || DEFAULT_CONFIG.MAX_POPUP_FIELDS;
    const suggestedPopupFields = [
      ...highImportanceFields.slice(0, maxFields),
      ...mediumImportanceFields.slice(0, Math.max(0, maxFields - highImportanceFields.length))
    ].map(f => f.label);
    
    // Find specific field types
    const titleField = fields.find(f => f.category === 'identification' && f.importance === 'high')?.fieldId;
    const descriptionField = fields.find(f => f.category === 'description' && f.importance === 'high')?.fieldId;
    const dateFields = fields.filter(f => f.category === 'date').map(f => f.fieldId);
    const mediaFields = fields.filter(f => f.category === 'media').map(f => f.fieldId);
    const relationshipFields = fields.filter(f => f.category === 'relationship').map(f => f.fieldId);
    
    // Generate field mapping
    const suggestedFieldMapping: FieldMapping = {
      id: DEFAULT_CONFIG.DEFAULT_FIELD_MAPPING_FALLBACKS,
      name: titleField ? [titleField] : fields.filter(f => f.category === 'identification').map(f => f.fieldId),
      description: descriptionField ? [descriptionField] : fields.filter(f => f.category === 'description').map(f => f.fieldId),
      created_at: dateFields.length > 0 ? dateFields : DEFAULT_CONFIG.DEFAULT_DATE_FALLBACKS,
      geometry: DEFAULT_CONFIG.DEFAULT_GEOMETRY_FIELD
    };

    const result: FormAnalysisResult = {
      formId,
      formName: form.name || `Form ${formId}`,
      fields: visibleFields,
      suggestedFieldMapping,
      suggestedPopupFields,
      titleField,
      descriptionField,
      dateFields,
      mediaFields,
      relationshipFields
    };

    console.log(`[FieldImportanceGenerator] Analysis complete for ${form.name}:`, {
      totalFields: fields.length,
      visibleFields: visibleFields.length,
      highImportance: highImportanceFields.length,
      mediumImportance: mediumImportanceFields.length,
      suggestedPopupFields: suggestedPopupFields.length
    });

    return result;
    
  } catch (error) {
    console.error(`[FieldImportanceGenerator] Error analyzing form ${formId}:`, error);
    throw error;
  }
};

/**
 * Analyze multiple forms and generate comprehensive configuration
 */
export const analyzeForms = async (
  formIds: string[],
  userProfile: any,
  options: ConfigGenerationOptions = {}
): Promise<FormAnalysisResult[]> => {
  console.log(`[FieldImportanceGenerator] Analyzing ${formIds.length} forms`);
  
  const results: FormAnalysisResult[] = [];
  
  for (const formId of formIds) {
    try {
      const result = await analyzeForm(formId, userProfile, options);
      results.push(result);
    } catch (error) {
      console.warn(`[FieldImportanceGenerator] Failed to analyze form ${formId}:`, error);
      // Continue with other forms
    }
  }
  
  console.log(`[FieldImportanceGenerator] Successfully analyzed ${results.length}/${formIds.length} forms`);
  return results;
};

/**
 * Generate endpoint configuration from form analysis
 */
export const generateEndpointConfig = (analysis: FormAnalysisResult): any => {
  return {
    name: analysis.formName,
    tableName: analysis.formName.toLowerCase().replace(/[^a-z0-9]/g, '_'),
    formId: analysis.formId,
    dataSourceType: 'fulcrum',
    dataSourceConfig: {
      fieldMapping: analysis.suggestedFieldMapping,
      popupFields: analysis.suggestedPopupFields,
      titleField: analysis.titleField,
      descriptionField: analysis.descriptionField
    },
    dateConfig: analysis.dateFields.length > 0 ? {
      dateField: analysis.dateFields[0],
      endDateField: analysis.dateFields[1] || null,
      showCalendarByDefault: true
    } : undefined,
    hasMedia: analysis.mediaFields.length > 0,
    hasRelationships: analysis.relationshipFields.length > 0
  };
};

/**
 * Auto-discover forms and generate complete configuration
 */
export const autoGenerateConfiguration = async (
  userProfile: any,
  options: ConfigGenerationOptions = {}
): Promise<any> => {
  try {
    console.log('[FieldImportanceGenerator] Starting auto-configuration generation');
    
    // Fetch all available forms
    const formsResponse = await fetchFulcrumForms(userProfile);
    const forms = formsResponse.forms || formsResponse;
    
    if (!forms || !Array.isArray(forms)) {
      throw new Error('No forms found or invalid response from Fulcrum API');
    }
    
    console.log(`[FieldImportanceGenerator] Found ${forms.length} forms to analyze`);
    
    // Analyze all forms
    const formIds = forms.map((form: any) => form.id);
    const analyses = await analyzeForms(formIds, userProfile, options);
    
    // Generate endpoint configurations
    const endpoints = analyses.map(generateEndpointConfig);
    
    // Generate complete configuration structure
    const config = {
      name: "Auto-generated Configuration",
      description: "Generated by Field Importance Generator",
      version: "1.0.0",
      generatedAt: new Date().toISOString(),
      apiEndpoints: endpoints,
      analysisResults: analyses // Include detailed analysis for review
    };
    
    console.log(`[FieldImportanceGenerator] Generated configuration with ${endpoints.length} endpoints`);
    return config;
    
  } catch (error) {
    console.error('[FieldImportanceGenerator] Error in auto-configuration generation:', error);
    throw error;
  }
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

export const fieldImportanceGenerator = {
  analyzeField,
  analyzeForm,
  analyzeForms,
  generateEndpointConfig,
  autoGenerateConfiguration
};